import { useState, useEffect, useCallback } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useScope } from "../../../container/SelectProjectCompanyContainer"
import Send from "../../communication"
import { useQuery } from "@tanstack/react-query"

// Using backend endpoints instead of direct Evolution API access
const BACKEND_API_BASE = Send.server

export const useWhatsAppAccess = () => {
  const { scope } = useScope()
  const dispatch = useDispatch()
  const [qrCode, setQRCode] = useState(null)
  const [connectionStatus, setConnectionStatus] = useState('checking') // 'checking', 'connected', 'disconnected', 'generating_qr'
  const [error, setError] = useState(null)

  // Get current user from Redux store
  const user = useSelector((state) => state.app.user?.user || {})
  
  // Generate instance name following the pattern: "{user.external_data.bpo.id} - playbpo"
  const getInstanceName = useCallback(() => {
    const bpoId = user?.external_data?.bpo?.id || scope?.id || 'default'
    return `${bpoId} - playbpo`
  }, [user, scope])

  // Check if WhatsApp instance exists and is connected
  const checkInstanceStatus = useCallback(async () => {
    try {
      setError(null)
      const instanceName = getInstanceName()

      const data = await Send.post('/evolution-api/instance-status', { instanceName })

      if (data?.qrcode) {
        // QR code available - need to scan
        setQRCode(data.qrcode)
        setConnectionStatus('generating_qr')
        return false
      } else if (data?.status === 'connected') {
        // Instance connected
        setConnectionStatus('connected')
        setQRCode(null)
        return true
      } else {
        // Instance exists but not connected
        setConnectionStatus('disconnected')
        setQRCode(null)
        return false
      }
    } catch (error) {
      console.error('Error checking WhatsApp instance:', error)
      setError(error.message)
      setConnectionStatus('disconnected')
      return false
    }
  }, [getInstanceName])

  // Create new WhatsApp instance
  const createInstance = useCallback(async () => {
    try {
      setError(null)
      setConnectionStatus('generating_qr')
      const instanceName = getInstanceName()

      const data = await Send.post('/evolution-api/create-instance', { instanceName })

      if (data?.qrcode) {
        setQRCode(data.qrcode)
        return true
      } else {
        throw new Error('QR code not received')
      }
    } catch (error) {
      console.error('Error creating WhatsApp instance:', error)
      setError(error.message)
      setConnectionStatus('disconnected')
      return false
    }
  }, [getInstanceName])

  // Refresh connection status
  const refreshStatus = useCallback(() => {
    return checkInstanceStatus()
  }, [checkInstanceStatus])

  // React Query for periodic status checking
  const { data: isConnected, isLoading, refetch } = useQuery({
    queryKey: ['whatsapp-status', getInstanceName()],
    queryFn: checkInstanceStatus,
    refetchInterval: connectionStatus === 'generating_qr' ? 5000 : 30000, // Check every 5s when generating QR, every 30s otherwise
    refetchOnWindowFocus: true,
    staleTime: 10000, // Consider data stale after 10 seconds
    retry: 3,
    retryDelay: 1000
  })

  // Check access - returns true if user can access chat
  const hasAccess = useCallback(() => {
    // For development/testing, you might want to bypass this check
    // return true
    
    return connectionStatus === 'connected'
  }, [connectionStatus])

  // Generate new QR code
  const generateQR = useCallback(async () => {
    setConnectionStatus('checking')
    const connected = await checkInstanceStatus()
    
    if (!connected) {
      await createInstance()
    }
  }, [checkInstanceStatus, createInstance])

  return {
    // Status
    connectionStatus,
    isConnected: connectionStatus === 'connected',
    isLoading,
    hasAccess: hasAccess(),
    
    // QR Code
    qrCode,
    generateQR,
    
    // Actions  
    refreshStatus,
    refetch,
    
    // Instance info
    instanceName: getInstanceName(),
    
    // Error handling
    error,
    clearError: () => setError(null)
  }
}